import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { set } from "react-hook-form";

/** Summary → Loan Details Edit: collapse co-applicant accordions so only loan/collateral fields show first. */
export const LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY = "__LOAN_DETAILS_LOAN_ONLY__";

export type EntranceExamSection = "core" | "language";

export interface EntranceExamEntry {
  ids: number[];
  fields: any[];
}

export interface EntranceExamEntriesState {
  core: EntranceExamEntry[];
  language: EntranceExamEntry[];
}

interface StepState {
  horizontalSteps: any[];
  displayHorizontalSteps: any[];
  completedHorizontalIndex?: number | null;
  activeHorizontalIndex: number;
  activeVerticalIndex: { [horizontalIndex: number]: number };
  formData: Record<string, any>;
  notInList: string[];
  isMobileVerified: boolean;
  isEmailVerified: boolean;
  loading: boolean;
  isFetchingFormJson: boolean;
  products: { code: string; name: string }[];
  selectedProduct?: string;
  trackingId: string;
  templateId: number;
  isBypassLogin: boolean;
  aadhaarApplicationNo: string;
  hasPan: string;
  panNumber: string;
  isPanVerified: boolean;
  adharMandatory: boolean;
  showAadhaar: boolean;
  lastPanNumber: string;
  lastVerifiedPhoneNumber: string;
  lastVerifiedEmail: string;
  isDocumentVerified: boolean;
  isEligibilityCheckSubmitted: boolean;
  aadhaarVerifiedDetails: {};
  aadhaarVerifiedDetailsCoApplicant: { [coAppKey: string]: any };
  coApplicantVerification: {
    [index: string]: {
      panVerified?: boolean;
      lastPanNumber?: string;
      emailVerified?: boolean;
      lastVerifiedEmail?: string;
      mobileVerified?: boolean;
      lastVerifiedMobileNumber?: string;
      aadhaarSeedingStatus?: boolean | null;
    };
  };
  currentCoApplicantIdx: string | null;
  extraCourses: any[];
  entranceExamEntries: EntranceExamEntriesState;
  cgpaModes: { [courseNum: string]: boolean };
  extraCoApplicant: any[];
  breStatus: string;
  breData: any[];
  fetchUserApplicationData: any[];
  isEligible: boolean;
  isProvisionalOffer: boolean;
  consentApplicationNo: string;
  partnerStartedJourney: boolean;
  documentUploadId: string;
  isDocumentVerifiedCoApplicant: {};
  aadhaarApplicationNoCoApplicant?: Record<string, string>;
  digiLockerLoaderId: string[];
  documentApplicantType: string;
  aggregatorDocId: string;
  accountAggregatorMobile: string;
  languageExamId: string;
  coreExamId: string;
  languageExamName: string;
  coreExamName: string;
  addedMoreDocumentData: {};
  coApplicantZipCodeData: { [coAppKey: string]: any };
  loanDetailCoApplicantCollapseValue: string;
  checkIsLeadApiInvoked: any;
  leadSequenceId: any;
  assignedSMIdValue: any;
  applicantId: string;
  applicationId: string;
  aadhaarSeedingStatus: boolean | null;
  verificationMessage: {};
  applicationSource: string | null;
  disableAddMoreCoApplicant: boolean;
  savedBasicCoApplicants: { [coAppKey: string]: boolean };
  savedAddressCoApplicants: { [coAppKey: string]: boolean };
  /** Raw PII public key from `getPiiKey` (`p_k`); sanitized when used for RSA. */
  encryptionKey: string;
  customerConsentTime: string;
  /** Summary page: user clicked Edit — show HorizontalLinearStepper form and hide matching ApplicationDetails row. */
  summaryApplicationEditActive: boolean;
  /**
   * Summary: inline form under "Co-Applicant" is shown only when user opened edit from a co-applicant card
   * (not when editing Loan Details under Applicant & Loan — same step indices would match otherwise).
   */
  summaryEditOpenedFromCoApplicantSection: boolean;
  /** After final co-applicant save, show summary cards in view-only mode and suppress Edit actions. */
  coApplicantSummaryViewOnly: boolean;
  /** Monotonic counter to request Formik submit from summary-level Save action. */
  summarySaveRequestId: number;
  /** Summary header Save button loading state while the inline form submits. */
  summaryFormSaveInProgress: boolean;
  /** Summary Reference/Collateral split rows: open the shared form on Reference (0) or Collateral (1). */
  summaryReferenceCollateralEditTab: number | null;
  /** Increment after summary saves so completion-rate API is fetched again. */
  completionSummaryRefreshNonce: number;
  /**
   * Increment after co-applicant `save-co-applicant` succeeds so HorizontalLinearStepper
   * can re-run getFormJson even when SUMMARY already has horizontalSteps.
   */
  formJsonRefreshNonce: number;
}

const initialState: StepState = {
  horizontalSteps: [],
  displayHorizontalSteps: [],
  activeHorizontalIndex: 0,
  activeVerticalIndex: {},
  notInList: [],
  completedHorizontalIndex: null,
  // activeHorizontalIndex: 1, // Start from horizontal step 1
  // activeVerticalIndex: { "0": 0, "1": 5, "2": 0, "3": 0, "4": 0, "5": 0 }, // Start from vertical step 0 of horizontal step 1
  formData: {},
  isMobileVerified: false,
  isEmailVerified: false,
  loading: true,
  isFetchingFormJson: false,
  products: [],
  selectedProduct: "", // or undefined
  trackingId: "",
  templateId: 0,
  isBypassLogin: false,
  aadhaarApplicationNo: "",
  hasPan: "",
  panNumber: "",
  isPanVerified: false,
  adharMandatory: false,
  showAadhaar: false,
  lastPanNumber: "",
  lastVerifiedPhoneNumber: "",
  lastVerifiedEmail: "",
  isDocumentVerified: false,
  isEligibilityCheckSubmitted: false,
  aadhaarVerifiedDetails: {},
  aadhaarVerifiedDetailsCoApplicant: {},
  coApplicantVerification: {},
  currentCoApplicantIdx: null,
  extraCourses: [],
  entranceExamEntries: {
    core: [],
    language: [],
  },
  cgpaModes: {},
  extraCoApplicant: [],
  breStatus: "",
  breData: [],
  fetchUserApplicationData: [],
  isEligible: false,
  isProvisionalOffer: false,
  consentApplicationNo: "",
  partnerStartedJourney: false,
  documentUploadId: "",
  isDocumentVerifiedCoApplicant: {},
  aadhaarApplicationNoCoApplicant: {},
  digiLockerLoaderId: [],
  documentApplicantType: "",
  aggregatorDocId: "",
  accountAggregatorMobile: "",
  languageExamId: "",
  coreExamId: "",
  languageExamName: "",
  coreExamName: "",
  addedMoreDocumentData: {},
  coApplicantZipCodeData: {},
  loanDetailCoApplicantCollapseValue: "CoApplicant1",
  checkIsLeadApiInvoked: {
    ASSIGN_SM: false,
    CREATE_LEAD: false,
    LEAD_TO_APPLICATION: false,
  },
  leadSequenceId: "",
  assignedSMIdValue: "",
  applicantId: "",
  applicationId: "",
  aadhaarSeedingStatus: null,
  verificationMessage: {},
  applicationSource: "",
  disableAddMoreCoApplicant: false,
  savedBasicCoApplicants: {},
  savedAddressCoApplicants: {},
  encryptionKey: "",
  customerConsentTime: "",
  summaryApplicationEditActive: false,
  summaryEditOpenedFromCoApplicantSection: false,
  coApplicantSummaryViewOnly: false,
  summarySaveRequestId: 0,
  summaryFormSaveInProgress: false,
  summaryReferenceCollateralEditTab: null,
  completionSummaryRefreshNonce: 0,
  formJsonRefreshNonce: 0,
};

const stepperSlice = createSlice({
  name: "stepper",
  initialState,
  reducers: {
    setHorizontalSteps: (state, action: PayloadAction<any[]>) => {
      state.horizontalSteps = action.payload;
    },
    setDisplayHorizontalSteps: (state, action: PayloadAction<any[]>) => {
      state.displayHorizontalSteps = action.payload;
    },
    setProducts: (
      state,
      action: PayloadAction<{ code: string; name: string }[]>
    ) => {
      state.products = action.payload;
    },
    setSelectedProduct: (state, action: PayloadAction<string>) => {
      state.selectedProduct = action.payload;
    },
    setIsMobileVerified: (state, action: PayloadAction<boolean>) => {
      state.isMobileVerified = action.payload;
    },
    setIsEmailVerified: (state, action: PayloadAction<boolean>) => {
      state.isEmailVerified = action.payload;
    },
    nextVerticalStep: (state) => {
      const hIndex = state.activeHorizontalIndex;
      const verticals = state.horizontalSteps[hIndex]?.verticalSteps || [];
      const currentVertical = state.activeVerticalIndex[hIndex] || 0;

      if (currentVertical < verticals.length - 1) {
        state.activeVerticalIndex[hIndex] = currentVertical + 1;
      } else if (hIndex < state.horizontalSteps.length - 1) {
        state.activeHorizontalIndex++;
        if (
          state.activeVerticalIndex[state.activeHorizontalIndex] === undefined
        ) {
          state.activeVerticalIndex[state.activeHorizontalIndex] = 0;
        }
      }
    },
    prevVerticalStep: (state) => {
      const hIndex = state.activeHorizontalIndex;
      const currentVertical = state.activeVerticalIndex[hIndex] || 0;
      if (currentVertical > 0) {
        state.activeVerticalIndex[hIndex] = currentVertical - 1;
      } else if (hIndex > 0) {
        state.activeHorizontalIndex--;
        const prevHIndex = state.activeHorizontalIndex;
        const prevVerticals =
          state.horizontalSteps[prevHIndex]?.verticalSteps || [];
        state.activeVerticalIndex[prevHIndex] =
          prevVerticals.length > 0 ? prevVerticals.length - 1 : 0;
      }
    },
    updateFormData: (state, action: PayloadAction<Record<string, any>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    clearFormData: (state) => {
      state.formData = {};
    },
    setCompletedHorizontalIndex: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.completedHorizontalIndex = action.payload;
    },
    setActiveHorizontalIndex: (state, action: PayloadAction<number>) => {
      state.activeHorizontalIndex = action.payload;
      if (state.activeVerticalIndex[action.payload] === undefined) {
        state.activeVerticalIndex[action.payload] = 0;
      }
    },
    setActiveVerticalIndex: (state, action: PayloadAction<number>) => {
      state.activeVerticalIndex[state.activeHorizontalIndex] = action.payload;
    },
    clearActiveIndex: (state) => {
      state.activeVerticalIndex = {};
    },
    setFormJson: (
      state,
      action: PayloadAction<{
        horizontalSteps: any[];
        templateId: number;
        section: EntranceExamSection;
        entries: EntranceExamEntry[];
      }>
    ) => {
      state.horizontalSteps = action.payload.horizontalSteps || [];
      state.templateId = action.payload.templateId;

      // Only reset if not already set
      if (Object.keys(state.activeVerticalIndex).length === 0) {
        state.activeHorizontalIndex = 0;
        state.activeVerticalIndex = {};
        if (action.payload.horizontalSteps) {
          action.payload.horizontalSteps.forEach((_: any, idx: number) => {
            state.activeVerticalIndex[idx] = 0;
          });
        }
      }
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsFetchingFormJson: (state, action: PayloadAction<boolean>) => {
      state.isFetchingFormJson = action.payload;
    },
    resetStepperForAssistedLead: (state, action: PayloadAction<string>) => ({
      ...initialState,
      trackingId: action.payload || "",
    }),
    setTrackingId: (state, action) => {
      if (state.trackingId && state.trackingId !== action.payload) {
        state.formData = {};
        state.isMobileVerified = false;
        state.isEmailVerified = false;
        state.lastVerifiedPhoneNumber = "";
        state.lastVerifiedEmail = "";
        state.panNumber = "";
        state.isPanVerified = false;
        state.lastPanNumber = "";
        state.isDocumentVerified = false;
        state.aadhaarApplicationNo = "";
        state.coApplicantVerification = {};
      }
      state.trackingId = action.payload;
    },
    setApplicationId: (state, action) => {
      state.applicationId = action.payload;
    },
    setApplicantId: (state, action) => {
      state.applicantId = action.payload;
    },
    setIsBypassLogin: (state, action: PayloadAction<boolean>) => {
      state.isBypassLogin = action.payload;
    },
    setAadhaarApplicationNo: (state, action) => {
      state.aadhaarApplicationNo = action.payload;
    },
    setHasPan: (state, action) => {
      state.hasPan = action.payload;
    },
    setPanNumber: (state, action) => {
      state.panNumber = action.payload;
    },
    setIsPanVerified: (state, action) => {
      state.isPanVerified = action.payload;
    },
    setAadhaarMandatory: (state, action) => {
      state.adharMandatory = action.payload;
    },
    setShowAadhaar: (state, action) => {
      state.showAadhaar = action.payload;
    },
    setLastPanNumber: (state, action) => {
      state.lastPanNumber = action.payload;
    },
    setLastVerifiedPhoneNumber: (state, action) => {
      state.lastVerifiedPhoneNumber = action.payload;
    },
    setLastVerifiedEmail: (state, action) => {
      state.lastVerifiedEmail = action.payload;
    },
    setIsDocumentVerified: (state, action) => {
      state.isDocumentVerified = action.payload;
    },
    setIsEligibilityCheckSubmitted: (state, action) => {
      state.isEligibilityCheckSubmitted = action.payload;
    },
    setNotInList: (state, action) => {
      state.notInList = [...state.notInList, action.payload];
    },
    removeNotInList: (state, action) => {
      state.notInList = state.notInList.filter(
        (item) => item !== action.payload
      );
    },
    clearNotInList: (state) => {
      state.notInList = [];
    },
    setNotInListFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setAadhaarVerifiedDetails: (state, action) => {
      state.aadhaarVerifiedDetails = action.payload;
    },
    setAadhaarVerifiedDetailsCoApplicant: (state, action) => {
      const { coAppKey, details } = action.payload;
      state.aadhaarVerifiedDetailsCoApplicant = {
        ...state.aadhaarVerifiedDetailsCoApplicant,
        [coAppKey]: details,
      };
    },
    setDobFormValue: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setCoApplicantVerification: (
      state,
      action: PayloadAction<{
        index: string;
        data: {
          panVerified?: boolean;
          lastPanNumber?: string;
          emailVerified?: boolean;
          lastVerifiedEmail?: string;
          mobileVerified?: boolean;
          lastVerifiedMobileNumber?: string;
          aadhaarSeedingStatus?: boolean;
        };
      }>
    ) => {
      const { index, data } = action.payload;
      // Always merge previous state for this co-applicant index
      state.coApplicantVerification[index] = {
        ...(state.coApplicantVerification[index] || {}),
        ...data,
      };
    },
    setCurrentCoApplicantIdx: (state, action: PayloadAction<string | null>) => {
      state.currentCoApplicantIdx = action.payload;
    },
    removeCoApplicantVerification(state, action: PayloadAction<string>) {
      delete state.coApplicantVerification[action.payload];
    },
    setCgpaMode(
      state,
      action: PayloadAction<{ courseNum: string; mode: boolean }>
    ) {
      state.cgpaModes[action.payload.courseNum] = action.payload.mode;
    },
    resetCgpaModes(state) {
      state.cgpaModes = {};
    },
    clearCourseFieldValues(state, action: PayloadAction<string[]>) {
      action.payload.forEach((fieldName) => {
        // If you store values in Redux, clear them here
        if (state.formData) {
          state.formData[fieldName] = "";
        }
      });
    },
    setBreStatus: (state, action) => {
      state.breStatus = action.payload;
    },
    setBreData: (state, action) => {
      state.breData = action.payload;
    },
    setFetchUserApplicationData: (state, action) => {
      state.fetchUserApplicationData = action.payload;
    },
    setIsEligible: (state, action) => {
      state.isEligible = action.payload;
    },
    setIsProvisionalOffer: (state, action) => {
      state.isProvisionalOffer = action.payload;
    },
    setConsentApplicationNo: (state, action) => {
      state.consentApplicationNo = action.payload;
    },
    setDocumentUploadId: (state, action) => {
      state.documentUploadId = action.payload;
    },
    setIsDocumentVerifiedCoApplicant: (state, action) => {
      const { coAppKey, value } = action.payload;
      state.isDocumentVerifiedCoApplicant = {
        ...state.isDocumentVerifiedCoApplicant,
        [coAppKey]: value,
      };
    },
    setAadhaarApplicationNoCoApplicant: (state, action) => {
      const { coAppKey, applicationNo } = action.payload;
      if (!state.aadhaarApplicationNoCoApplicant) {
        state.aadhaarApplicationNoCoApplicant = {};
      }
      state.aadhaarApplicationNoCoApplicant[coAppKey] = applicationNo;
    },
    removeAadhaarVerifiedDetailsCoApplicant: (
      state,
      action: PayloadAction<string>
    ) => {
      // action.payload is the coAppKey, e.g., 'CoApplicant2'
      if (
        state.aadhaarVerifiedDetailsCoApplicant &&
        state.aadhaarVerifiedDetailsCoApplicant[action.payload]
      ) {
        const newObj = { ...state.aadhaarVerifiedDetailsCoApplicant };
        delete newObj[action.payload];
        state.aadhaarVerifiedDetailsCoApplicant = newObj;
      }
    },
    removeIsDocumentVerifiedCoApplicant: (
      state,
      action: PayloadAction<string>
    ) => {
      // action.payload is the coAppKey, e.g., 'CoApplicant2'
      const obj = state.isDocumentVerifiedCoApplicant as Record<string, any>;
      if (obj && obj[action.payload] !== undefined) {
        const newObj = { ...obj };
        delete newObj[action.payload];
        state.isDocumentVerifiedCoApplicant = newObj;
      }
    },
    removeAadhaarApplicationNoCoApplicant: (
      state,
      action: PayloadAction<{ coAppNum: string; coAppKey: string }>
    ) => {
      // Remove both numeric and string keys for this co-applicant
      const obj = state.aadhaarApplicationNoCoApplicant as Record<string, any>;
      if (obj) {
        const newObj = { ...obj };
        delete newObj[action.payload.coAppNum];
        delete newObj[action.payload.coAppKey];
        state.aadhaarApplicationNoCoApplicant = newObj;
      }
    },
    setDigiLockerLoaderId: (state, action) => {
      state.digiLockerLoaderId = [...state.digiLockerLoaderId, action.payload];
    },
    removeDigiLockerLoaderId: (state) => {
      state.digiLockerLoaderId = [];
    },
    setDocumentApplicantType: (state, action) => {
      state.documentApplicantType = action.payload;
    },
    setPartnerStartedJourney: (state, action) => {
      state.partnerStartedJourney = action.payload;
    },
    setAggregatorDocId: (state, action) => {
      state.aggregatorDocId = action.payload;
    },
    setAccountAggregatorMobile: (state, action) => {
      state.accountAggregatorMobile = action.payload;
    },
    setLanguageExamId: (state, action: PayloadAction<string>) => {
      state.languageExamId = action.payload;
    },
    setCoreExamId: (state, action: PayloadAction<string>) => {
      state.coreExamId = action.payload;
    },
    setLanguageExamName: (state, action: PayloadAction<string>) => {
      state.languageExamName = action.payload;
    },
    setCoreExamName: (state, action: PayloadAction<string>) => {
      state.coreExamName = action.payload;
    },
    setAddedMoreDocumentData: (state, action) => {
      state.addedMoreDocumentData = action.payload;
    },
    clearCoApplicantVerification(state) {
      state.coApplicantVerification = {};
    },
    clearIsDocumentVerifiedCoApplicant(state) {
      state.isDocumentVerifiedCoApplicant = {};
    },
    clearAadhaarApplicationNoCoApplicant(state) {
      state.aadhaarApplicationNoCoApplicant = {};
    },
    clearAadhaarVerifiedDetails(state) {
      state.aadhaarVerifiedDetails = {};
    },
    clearAadhaarVerifiedDetailsCoApplicant(state) {
      state.aadhaarVerifiedDetailsCoApplicant = {};
    },
    removeFormDataKeys: (state, action) => {
      const keys: string[] = action.payload;
      keys.forEach((key) => {
        delete state.formData[key];
      });
    },

    setCoApplicantZipCodeData: (state, action) => {

      console.log(action.payload, "action.payload");

      // Deep merge to preserve nested current/permanent data for each co-applicant
      const newData = { ...state.coApplicantZipCodeData };
      
      // Merge each co-applicant's data properly
      Object.keys(action.payload).forEach((key) => {
        if (key === 'coAppKey') {
          // Skip the coAppKey property as it's not part of the data structure
          return;
        }
        
        if (action.payload[key] && typeof action.payload[key] === 'object') {
          // Deep merge: preserve existing current/permanent if new data doesn't have them
          if (newData[key]) {
            newData[key] = {
              current: action.payload[key].current !== undefined 
                ? action.payload[key].current 
                : newData[key].current,
              permanent: action.payload[key].permanent !== undefined 
                ? action.payload[key].permanent 
                : newData[key].permanent,
            };
          } else {
            newData[key] = { ...action.payload[key] };
          }
        } else {
          newData[key] = action.payload[key];
        }
      });
      
      state.coApplicantZipCodeData = newData;
    },

    setLoanDetailCoApplicantCollapseValue: (state, action) => {
      state.loanDetailCoApplicantCollapseValue = action.payload;
    },
    clearSetLoanDetailCoApplicantCollapseValue: (state) => {
      state.loanDetailCoApplicantCollapseValue = "CoApplicant1";
    },
    setCheckIsLeadApiInvoked: (state, action) => {
      state.checkIsLeadApiInvoked[action.payload.key] = action.payload.value;
    },
    clearCheckIsLeadApiInvoked: (state) => {
      state.checkIsLeadApiInvoked = {
        ASSIGN_SM: false,
        CREATE_LEAD: false,
        LEAD_TO_APPLICATION: false,
      };
    },
    setLeadSequenceId: (state, action) => {
      state.leadSequenceId = action.payload;
    },
    setAssignedSMIdValue: (state, action) => {
      state.assignedSMIdValue = action.payload;
    },
    setAadhaarSeedingStatus: (state, action) => {
      state.aadhaarSeedingStatus = action.payload;
    },
    setVerificationMessage: (
      state,
      action: PayloadAction<{
        index?: string | number;
        applicantType: "applicant" | "coapplicant" | "";
        field: "pan" | "aadhar" | "";
        status: "success" | "error" | "server-error" | "";
        message: string;
      }>
    ) => {
      state.verificationMessage = action.payload;
    },
    setApplicationSource: (state, action) => {
      state.applicationSource = action.payload;
    },
    setDisableAddMoreCoApplicant: (state, action: PayloadAction<boolean>) => {
      state.disableAddMoreCoApplicant = action.payload;
    },
    setSavedBasicForCoApplicant: (
      state,
      action: PayloadAction<{ coAppKey: string; value: boolean }>
    ) => {
      state.savedBasicCoApplicants = {
        ...state.savedBasicCoApplicants,
        [action.payload.coAppKey]: action.payload.value,
      };
    },
    removeSavedBasicForCoApplicant: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (
        state.savedBasicCoApplicants &&
        state.savedBasicCoApplicants[key] !== undefined
      ) {
        const newObj = { ...state.savedBasicCoApplicants };
        delete newObj[key];
        state.savedBasicCoApplicants = newObj;
      }
    },
    setSavedAddressForCoApplicant: (
      state,
      action: PayloadAction<{ coAppKey: string; value: boolean }>
    ) => {
      state.savedAddressCoApplicants = {
        ...state.savedAddressCoApplicants,
        [action.payload.coAppKey]: action.payload.value,
      };
    },
    clearSavedBasicCoApplicants: (state) => {
      state.savedBasicCoApplicants = {};
    },
    setEncryptionKey: (state, action: PayloadAction<string>) => {
      state.encryptionKey = action.payload;
    },
    setCustomerConsentTime: (state, action) => {
      state.customerConsentTime = action.payload;
    },
    setSummaryApplicationEditActive: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.summaryApplicationEditActive = action.payload;
      if (!action.payload) {
        state.summaryEditOpenedFromCoApplicantSection = false;
        state.summaryReferenceCollateralEditTab = null;
      }
    },
    setSummaryEditOpenedFromCoApplicantSection: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.summaryEditOpenedFromCoApplicantSection = action.payload;
    },
    setCoApplicantSummaryViewOnly: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.coApplicantSummaryViewOnly = action.payload;
    },
    requestSummaryFormSave: (state) => {
      state.summarySaveRequestId += 1;
    },
    setSummaryFormSaveInProgress: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.summaryFormSaveInProgress = action.payload;
    },
    setSummaryReferenceCollateralEditTab: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.summaryReferenceCollateralEditTab = action.payload;
    },
    bumpCompletionSummaryRefreshNonce: (state) => {
      state.completionSummaryRefreshNonce += 1;
    },
    bumpFormJsonRefreshNonce: (state) => {
      state.formJsonRefreshNonce += 1;
    },
  },
});

// Export only actions and reducer from this file
export const {
  setHorizontalSteps,
  setDisplayHorizontalSteps,
  setSelectedProduct,
  nextVerticalStep,
  prevVerticalStep,
  updateFormData,
  setCompletedHorizontalIndex,
  setActiveHorizontalIndex,
  setFormJson,
  setLoading,
  setIsFetchingFormJson,
  resetStepperForAssistedLead,
  setProducts,
  setIsMobileVerified,
  setIsEmailVerified,
  setTrackingId,
  setApplicationId,
  setApplicantId,
  setIsBypassLogin,
  setAadhaarApplicationNo,
  setHasPan,
  setPanNumber,
  setIsPanVerified,
  setAadhaarMandatory,
  setShowAadhaar,
  setLastPanNumber,
  setLastVerifiedPhoneNumber,
  setLastVerifiedEmail,
  setIsDocumentVerified,
  setIsEligibilityCheckSubmitted,
  setActiveVerticalIndex,
  clearFormData,
  setNotInList,
  removeNotInList,
  setNotInListFormData,
  clearActiveIndex,
  setAadhaarVerifiedDetails,
  setAadhaarVerifiedDetailsCoApplicant,
  removeAadhaarVerifiedDetailsCoApplicant,
  setDobFormValue,
  setCoApplicantVerification,
  setCurrentCoApplicantIdx,
  clearNotInList,
  setCgpaMode,
  resetCgpaModes,
  clearCourseFieldValues,
  removeCoApplicantVerification,
  setBreStatus,
  setBreData,
  setFetchUserApplicationData,
  setIsEligible,
  setIsProvisionalOffer,
  setConsentApplicationNo,
  setDocumentUploadId,
  setIsDocumentVerifiedCoApplicant,
  setAadhaarApplicationNoCoApplicant,
  removeIsDocumentVerifiedCoApplicant,
  removeAadhaarApplicationNoCoApplicant,
  setDigiLockerLoaderId,
  removeDigiLockerLoaderId,
  setDocumentApplicantType,
  setPartnerStartedJourney,
  setAggregatorDocId,
  setAccountAggregatorMobile,
  setLanguageExamId,
  setCoreExamId,
  setLanguageExamName,
  setCoreExamName,
  clearCoApplicantVerification,
  clearIsDocumentVerifiedCoApplicant,
  clearAadhaarApplicationNoCoApplicant,
  clearAadhaarVerifiedDetails,
  clearAadhaarVerifiedDetailsCoApplicant,
  removeFormDataKeys,
  setAddedMoreDocumentData,
  setCoApplicantZipCodeData,
  setLoanDetailCoApplicantCollapseValue,
  setCheckIsLeadApiInvoked,
  setLeadSequenceId,
  clearCheckIsLeadApiInvoked,
  clearSetLoanDetailCoApplicantCollapseValue,
  setAssignedSMIdValue,
  setAadhaarSeedingStatus,
  setVerificationMessage,
  setApplicationSource,
  setDisableAddMoreCoApplicant,
  setSavedBasicForCoApplicant,
  removeSavedBasicForCoApplicant,
  setSavedAddressForCoApplicant,
  clearSavedBasicCoApplicants,
  setEncryptionKey,
  setCustomerConsentTime,
  setSummaryApplicationEditActive,
  setSummaryEditOpenedFromCoApplicantSection,
  setCoApplicantSummaryViewOnly,
  requestSummaryFormSave,
  setSummaryFormSaveInProgress,
  setSummaryReferenceCollateralEditTab,
  bumpCompletionSummaryRefreshNonce,
  bumpFormJsonRefreshNonce,
} = stepperSlice.actions;
export default stepperSlice.reducer;
